import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { CSSTransition } from 'react-transition-group';

const assignRef = (ref, node) => {
    if (typeof ref === 'function') {
        ref(node);
        return;
    }

    if (ref) {
        ref.current = node;
    }
};

const mergeRefs =
    (...refs) =>
    (node) => {
        refs.forEach((ref) => assignRef(ref, node));
    };

/**
 * React 19 removed ReactDOM.findDOMNode. react-transition-group still calls it
 * unless nodeRef is provided. Always use this wrapper instead of CSSTransition.
 */
const CSSTransitionWithRef = ({ children, nodeRef: nodeRefProp, transitionKey, ...props }) => {
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- a new key requires a new transition node ref
    const internalNodeRef = useMemo(() => ({ current: null }), [transitionKey]);
    const nodeRef = nodeRefProp ?? internalNodeRef;
    const child = React.Children.only(children);
    const childRef = child.props.ref;

    return (
        <CSSTransition key={transitionKey} nodeRef={nodeRef} {...props}>
            {React.cloneElement(child, {
                ref: mergeRefs(nodeRef, childRef),
            })}
        </CSSTransition>
    );
};

CSSTransitionWithRef.propTypes = {
    children: PropTypes.element.isRequired,
    nodeRef: PropTypes.shape({ current: PropTypes.any }),
    transitionKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default CSSTransitionWithRef;
